import React from 'react';
import { Dialog, DialogContent, Drawer, Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export type PromoSettings = {
  enabled?: boolean;
  showOncePerSession?: boolean;
  title?: string;
  message?: string;
  image?: string;
  images?: string[];
  imageInterval?: number;
  imagesLayout?: 'slideshow' | 'list';
  imagesLinks?: Record<string, string>;
  ctaText?: string;
  ctaUrl?: string;
  displayMode?: 'modal' | 'drawer';
  drawerSide?: 'left' | 'right' | 'top' | 'bottom';
};

interface PromoPopupProps {
  promo: PromoSettings | undefined;
  resolvePublicUrl: (p?: string) => string | undefined;
}

const sessionKey = 'promo_popup_shown_v1';

const PromoPopup: React.FC<PromoPopupProps> = ({ promo, resolvePublicUrl }) => {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!promo || promo.enabled === false) return;
    if (promo.showOncePerSession && sessionStorage.getItem(sessionKey) === '1') return;
    setOpen(true);
    if (promo.showOncePerSession) {
      try { sessionStorage.setItem(sessionKey, '1'); } catch {}
    }
  }, [promo]);

  const onClose = () => setOpen(false);

  const imagesArr = React.useMemo(() => {
    const arr = Array.isArray(promo?.images) ? promo!.images!.filter(Boolean) : [];
    if (arr.length > 0) return arr;
    return promo?.image ? [promo.image] : [];
  }, [promo?.images, promo?.image]);

  const [imgIdx, setImgIdx] = React.useState(0);
  React.useEffect(() => {
    setImgIdx(0);
    if (!open) return; // only animate when visible
    if (imagesArr.length <= 1) return;
    if (promo?.imagesLayout === 'list') return; // no rotation in list mode
    const intervalSec = Math.max(2, Math.min(60, Number(promo?.imageInterval) || 5));
    const id = window.setInterval(() => setImgIdx(i => (i + 1) % imagesArr.length), intervalSec * 1000);
    return () => window.clearInterval(id);
  }, [open, imagesArr, promo?.imageInterval, promo?.imagesLayout]);

  const imageUrl = resolvePublicUrl(imagesArr[imgIdx]);

  const isList = promo?.imagesLayout === 'list' && imagesArr.length > 1;

  const renderLinkedImg = (p: string, imgSx: any, key: React.Key) => {
    const url = promo?.imagesLinks && promo.imagesLinks[p];
    const Img = (
      <Box component="img" src={resolvePublicUrl(p)} alt={promo?.title || 'Promotion'} sx={imgSx} />
    );
    return url ? (
      <Box key={key} component="a" href={url} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
        {Img}
      </Box>
    ) : (
      <React.Fragment key={key}>{Img}</React.Fragment>
    );
  };

  const ImagesList = (
    <Box sx={{ px: 3, pb: 3 }}>
      <Box sx={{ display: 'flex', gap: 1.25, overflowX: 'auto', py: 0.5 }}>
        {imagesArr.map((p, i) => renderLinkedImg(p, { height: 160, width: 'auto', borderRadius: 2, boxShadow: 1, flexShrink: 0, objectFit: 'cover', display: 'block' }, p + i))}
      </Box>
    </Box>
  );

  const Content = (
    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, flex: 1, background: 'linear-gradient(180deg, #ffffff, #f8fafc)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
          <Typography variant="h5" fontWeight={800}>
            {promo?.title || 'Special Promotion'}
          </Typography>
          <IconButton onClick={onClose} sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </Box>
        {promo?.message && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
            {promo.message}
          </Typography>
        )}
        <Box>
          {promo?.ctaUrl && (
            <Button
              variant="contained"
              size="large"
              onClick={() => window.open(promo.ctaUrl!, '_blank', 'noopener,noreferrer')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
                py: 1,
                background: 'linear-gradient(90deg, #22c55e, #16a34a)',
                boxShadow: '0 10px 24px rgba(34,197,94,0.25)',
                '&:hover': { background: 'linear-gradient(90deg, #16a34a, #15803d)' },
              }}
            >
              {promo?.ctaText || 'Learn more'}
            </Button>
          )}
        </Box>
      </Box>
      {!isList && imageUrl && (
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          {(() => {
            const currentPath = imagesArr[imgIdx] || '';
            const link = promo?.imagesLinks && promo.imagesLinks[currentPath];
            const imgEl = (
              <Box component="img" src={imageUrl} alt={promo?.title || 'Promotion'} key={imgIdx} sx={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: 2, boxShadow: 1, display: 'block', transition: 'opacity .25s ease' }} />
            );
            return link ? (
              <Box component="a" href={link} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>{imgEl}</Box>
            ) : imgEl;
          })()}
        </Box>
      )}
      {isList && ImagesList}
    </Box>
  );

  if (promo?.displayMode === 'drawer') {
    const anchor = (promo.drawerSide || 'right') as any;
    const isVertical = anchor === 'top' || anchor === 'bottom';
    return (
      <Drawer
        anchor={anchor}
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: anchor === 'left' || anchor === 'right' ? 520 : '100%', md: anchor === 'left' || anchor === 'right' ? 560 : '100%' },
            maxWidth: anchor === 'left' || anchor === 'right' ? 620 : '100%',
            height: isVertical ? { xs: '70dvh', md: '60dvh' } : undefined,
            maxHeight: isVertical ? '85dvh' : undefined,
            borderRadius: anchor === 'left' ? '0 12px 12px 0' : anchor === 'right' ? '12px 0 0 12px' : '12px 12px 0 0',
            overflow: 'hidden',
            boxShadow: '0 24px 64px rgba(0,0,0,0.28)'
          }
        }}
        slotProps={{ backdrop: { sx: { backdropFilter: 'blur(3px)' } } as any }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: '100dvh' }}>
          {/* header for spacing on small screens */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, height: 0.5 }} />
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 3, flex: '0 0 auto', minWidth: 0, background: 'linear-gradient(180deg, #ffffff, #f8fafc)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                  <Typography variant="h5" fontWeight={800}>
                    {promo?.title || 'Special Promotion'}
                  </Typography>
                  <IconButton onClick={onClose} sx={{ ml: 1 }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                {promo?.message && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                    {promo.message}
                  </Typography>
                )}
                <Box>
                  {promo?.ctaUrl && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => window.open(promo.ctaUrl!, '_blank', 'noopener,noreferrer')}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        background: 'linear-gradient(90deg, #22c55e, #16a34a)',
                        boxShadow: '0 10px 24px rgba(34,197,94,0.25)',
                        '&:hover': { background: 'linear-gradient(90deg, #16a34a, #15803d)' },
                      }}
                    >
                      {promo?.ctaText || 'Learn more'}
                    </Button>
                  )}
                </Box>
              </Box>
              {!isList && imageUrl && (
                <Box sx={{ p: 3, pt: 2 }}>
                  {(() => {
                    const currentPath = imagesArr[imgIdx] || '';
                    const link = promo?.imagesLinks && promo.imagesLinks[currentPath];
                    const imgEl = (
                      <Box component="img" src={imageUrl} alt={promo?.title || 'Promotion'} key={imgIdx} sx={{ width: '100%', height: 'auto', maxHeight: isVertical ? '38dvh' : '56dvh', objectFit: isVertical ? 'contain' : 'cover', backgroundColor: isVertical ? 'rgba(0,0,0,0.03)' : 'transparent', borderRadius: 2, boxShadow: 1, display: 'block', transition: 'opacity .25s ease' }} />
                    );
                    return link ? (
                      <Box component="a" href={link} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>{imgEl}</Box>
                    ) : imgEl;
                  })()}
                </Box>
              )}
              {isList && (
                <Box sx={{ px: 3, pt: 2, pb: 3 }}>
                  {isVertical ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                      {imagesArr.map((p, i) => (
                        <Box key={p + i} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', boxShadow: 1, bgcolor: 'rgba(0,0,0,0.03)', height: '34dvh' }}>
                          {renderLinkedImg(p, { position: 'absolute', inset: 0, m: 'auto', maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }, p + i)}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gridAutoRows: 'auto', gap: 1.25 }}>
                      {imagesArr.map((p, i) => (
                        <Box key={p + i} sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 1, bgcolor: 'rgba(0,0,0,0.03)' }}>
                          {renderLinkedImg(p, { width: '100%', height: 'auto', display: 'block', objectFit: 'contain' }, p + i)}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 30px 80px rgba(0,0,0,0.35)'
        }
      }}
      slotProps={{ backdrop: { sx: { backdropFilter: 'blur(3px)' } } as any }}
    >
      <DialogContent sx={{ p: 0 }}>
        {Content}
      </DialogContent>
    </Dialog>
  );
};

export default PromoPopup;
